<template>
    <div class="header-actions-wrapper">
        <div class="menu-search-trigger" :class="{ 'is-active': selectedCount > 0 }" @click="openFilterModal">
            <FilterOutlined class="menu-search-icon" />
            <span class="menu-search-text">数据筛选</span>
            <span v-if="selectedCount > 0" class="menu-search-badge" :title="`已选 ${selectedCount} 个组织`">
        {{ selectedCount }}
      </span>
        </div>
        <div class="menu-search-trigger" @click="openSearchModal">
            <SearchOutlined class="menu-search-icon" />
            <span class="menu-search-text">搜索菜单</span>
        </div>
        <a-modal
                :visible="searchVisible"
                :footer="null"
                :width="600"
                :closable="false"
                :bodyStyle="{ padding: '12px 16px' }"
                wrap-class-name="menu-search-modal"
                @cancel="closeSearchModal"
        >
            <a-input
                    v-model:value="keyword"
                    placeholder="输入菜单名称搜索"
                    size="large"
                    allowClear
            >
                <template #prefix>
                    <SearchOutlined />
                </template>
            </a-input>
            <div class="menu-search-results" v-if="filteredMenuList.length">
                <div
                        v-for="item in filteredMenuList"
                        :key="item.path"
                        class="menu-search-item"
                        @click="navigateToMenu(item)"
                >
                    <component :is="item.icon" v-if="item.icon" class="menu-search-item-icon" />
                    <span class="menu-search-item-title">{{ item.title }}</span>
                    <span v-if="item.breadcrumb" class="menu-search-item-path">{{ item.breadcrumb }}</span>
                </div>
            </div>
            <a-empty v-else description="未找到匹配的菜单" />
        </a-modal>

        <a-modal
                :visible="filterVisible"
                :width="780"
                title="数据筛选"
                :confirmLoading="filterSubmitting"
                :maskClosable="false"
                wrap-class-name="data-filter-modal"
                okText="提交"
                cancelText="取消"
                @ok="submitFilter"
                @cancel="closeFilterModal"
        >
            <div class="data-filter-tip">选择您需要查看的部门数据（可多选）</div>
            <div class="data-filter-toolbar">
                <a-checkbox :indeterminate="filterIndeterminate" :checked="filterCheckedAll" @change="onCheckAllChange">
                    全选
                </a-checkbox>
                <a-button size="small" type="link" @click="clearFilterSelection">清空</a-button>
            </div>
            <div class="data-filter-tree">
                <a-spin :spinning="filterLoading">
                    <a-tree
                            v-if="filterTreeData.length"
                            :tree-data="filterTreeData"
                            :fieldNames="{ key: 'departmentId', title: 'departmentName' }"
                            checkable
                            :checkStrictly="false"
                            blockNode
                            :defaultExpandAll="false"
                            v-model:checkedKeys="filterCheckedKeys"
                    >
                        <template #title="dataRef">
                            <span>{{ dataRef.departmentName }}</span>
                            <a-button
                                    type="link"
                                    size="small"
                                    class="data-filter-tree-view"
                                    @click.stop="handleViewPromoters(dataRef)"
                            >
                                查看
                            </a-button>
                            <span
                                    v-if="dataRef.directUserCount != null || dataRef.subUserCount != null"
                                    class="data-filter-tree-counts"
                            >
                <span>直接 <b>{{ dataRef.directUserCount ?? 0 }}</b></span>
                <span>子部门 <b>{{ dataRef.subUserCount ?? 0 }}</b></span>
              </span>
                        </template>
                    </a-tree>
                    <a-empty v-else-if="!filterLoading" description="暂无可访问的部门" />
                </a-spin>
            </div>
<!--            <div v-if="lastFilterResult.length" class="data-filter-summary">-->
<!--                <div class="data-filter-summary-title">本次提交所选部门人数</div>-->
<!--                <ul class="data-filter-summary-list">-->
<!--                    <li v-for="d in lastFilterResult" :key="d.departmentId">-->
<!--                        <span class="data-filter-summary-name">{{ d.departmentName }}</span>-->
<!--                        <span class="data-filter-summary-counts">-->
<!--              <span>直接 <b>{{ d.directUserCount ?? 0 }}</b></span>-->
<!--              <span>子部门 <b>{{ d.subUserCount ?? 0 }}</b></span>-->
<!--            </span>-->
<!--                    </li>-->
<!--                </ul>-->
<!--            </div>-->
        </a-modal>

        <!-- 查看人员：当前部门下的人（只读） -->
        <a-modal
                :visible="promoterVisible"
                width="720px"
                :title="`「${promoterDeptName}」人员信息`"
                :footer="null"
                destroyOnClose
                :maskClosable="false"
                @cancel="closePromoterModal"
        >
            <a-table
                    :rowKey="(record) => record.id"
                    :dataSource="promoterList"
                    :columns="promoterColumns"
                    :loading="promoterLoading"
                    :pagination="false"
                    size="small"
            >
                <template #bodyCell="{ record, column }">
                    <template v-if="column.dataIndex === 'status'">
                        <span :class="['dot', promoterStatusClass(record.status)]">{{ promoterStatusText(record.status) }}</span>
                    </template>
                    <template v-else-if="column.dataIndex === 'manager'">
                        <span :class="['dot', managerClass(record.manager)]">{{ managerText(record.manager) }}</span>
                    </template>
                </template>
            </a-table>
            <a-empty v-if="!promoterLoading && promoterList.length === 0" description="该部门下暂无人员" />
        </a-modal>
    </div>
</template>

<script lang="ts" setup>
import { ref, computed, nextTick, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { FilterOutlined, SearchOutlined } from '@ant-design/icons-vue';
import { useMenuStore, useDepartmentFilterStore } from '@/store';
import { message } from 'ant-design-vue';
import {
    getDepartmentTree,
    getDepartmentSelectList,
    submitDepartmentFilter,
    listDepartmentPromoters,
} from '@/services/department';

const router = useRouter();
const menuStore = useMenuStore();
const departmentFilterStore = useDepartmentFilterStore();
const lastFilterResult = departmentFilterStore.lastFilterResult;
const searchVisible = ref(false);
const keyword = ref('');

const filterVisible = ref(false);
const filterLoading = ref(false);
const filterSubmitting = ref(false);
const filterTreeData = ref<any[]>([]);
const filterCheckedKeys = ref<any[]>([]);
const selectedCount = ref(0);

// 把后端树转成 a-tree 期望的字段（key/title/children），并收集所有可勾选节点
const flattenAllKeys = (nodes: any[]): any[] => {
    const keys: any[] = [];
    const walk = (list: any[]) => {
        (list || []).forEach((n) => {
            keys.push(n.departmentId);
            if (n.children?.length) walk(n.children);
        });
    };
    walk(nodes);
    return keys;
};

const allKeys = computed(() => flattenAllKeys(filterTreeData.value));

const filterCheckedAll = computed(() => {
    return allKeys.value.length > 0 && filterCheckedKeys.value.length === allKeys.value.length;
});

const filterIndeterminate = computed(() => {
    return (
        filterCheckedKeys.value.length > 0 && filterCheckedKeys.value.length < allKeys.value.length
    );
});

const onCheckAllChange = (e: any) => {
    filterCheckedKeys.value = e.target.checked ? [...allKeys.value] : [];
};

const clearFilterSelection = () => {
    filterCheckedKeys.value = [];
};

const isValidIconName = (icon: any) => {
    if (typeof icon !== 'string') return false;
    const name = icon.trim();
    if (!name) return false;
    if (name.startsWith('/') || name.startsWith('.') || name.includes(' ')) return false;
    return /^[A-Za-z][A-Za-z0-9_-]*$/.test(name);
};

const flatMenuList = computed(() => {
    const flat: any[] = [];
    const walk = (items: any[], parents: string[] = []) => {
        if (!Array.isArray(items)) return;
        items.forEach((item) => {
            if (item.path && item.renderMenu !== false && item.component) {
                flat.push({
                    title: item.title,
                    path: item.path,
                    icon: isValidIconName(item.icon) ? item.icon : '',
                    breadcrumb: parents.length ? parents.join(' / ') + ' / ' + item.title : item.title,
                });
            }
            if (item.children?.length) {
                walk(item.children, [...parents, item.title]);
            }
        });
    };
    walk(menuStore.menuList);
    return flat;
});

const filteredMenuList = computed(() => {
    const kw = keyword.value.trim();
    if (!kw) return flatMenuList.value;
    return flatMenuList.value.filter((item) => (item.title || '').includes(kw));
});

const openSearchModal = () => {
    searchVisible.value = true;
    keyword.value = '';
    nextTick(() => {
        const el = document.querySelector('.menu-search-modal .ant-input') as HTMLInputElement;
        el?.focus();
    });
};

const closeSearchModal = () => {
    searchVisible.value = false;
};

const navigateToMenu = (item: any) => {
    if (!item.path) {
        message.warning('该菜单没有配置路由');
        return;
    }
    router.push(item.path);
    closeSearchModal();
};

const openFilterModal = () => {
    filterVisible.value = true;
    // 每次打开都重新拉取最新组织树，确保弹框内 tree 是最新的
    fetchFilterTree();
    // 每次打开都重新拉取 /list/select，把返回的部门默认勾选到树上（回显数据）
    fetchSelectList();
};

const closeFilterModal = () => {
    filterVisible.value = false;
};

const fetchFilterTree = () => {
    filterLoading.value = true;
    getDepartmentTree()
        .then((res: any) => {
            filterLoading.value = false;
            if (res.code === 1) {
                filterTreeData.value = res.list || [];
            } else {
                message.error(res.tip || '加载组织树失败');
            }
        })
        .catch(() => {
            filterLoading.value = false;
        });
};

const submitFilter = async () => {
    filterSubmitting.value = true;
    try {
        const res: any = await submitDepartmentFilter(filterCheckedKeys.value);
        if (res.code === 1) {
            const ids = filterCheckedKeys.value;
            const list = res.list || [];
            selectedCount.value = list.length;
            // 写入全局筛选 store：业务页面 watch 该 store 自动重新拉数据
            departmentFilterStore.applyFilter(ids, list);
            message.success(res.tip || '提交成功');
            closeFilterModal();
        }
    } catch (e) {
        // http 拦截器已统一提示
    } finally {
        filterSubmitting.value = false;
    }
};

// 加载默认回显数据：调用 /list/select，把返回的部门默认勾选到树上，badge 显示勾选数
const fetchSelectList = async () => {
    try {
        const res: any = await getDepartmentSelectList();
        if (res.code === 1) {
            const ids = (res.list || []).map((d: any) => d.departmentId);
            filterCheckedKeys.value = ids;
            selectedCount.value = ids.length;
        }
    } catch (e) {
        // http 拦截器已统一提示，静默失败即可
    }
};

// —— 查看部门下人员（参考组织管理 👤 弹框） ——
const promoterVisible = ref(false);
const promoterLoading = ref(false);
const promoterList = ref<any[]>([]);
const promoterDeptName = ref('');

const promoterColumns = [
    { title: '角色', dataIndex: 'manager', width: 100, align: 'center' },
    { title: '姓名', dataIndex: 'promoterName', width: 120 },
    { title: '邀请码', dataIndex: 'inviteCode', width: 180 },
    { title: '手机号', dataIndex: 'phoneNumber', width: 160 },
    { title: '当前账号归属', dataIndex: 'currentDeptName', width: 160 },
    { title: '状态', dataIndex: 'status', width: 100 },
];

// 后端 status 0=禁用 1=正常 2=锁定
const promoterStatusText = (s: number) => ({ 0: '禁用', 1: '正常', 2: '锁定' }[s] ?? '-');
const promoterStatusClass = (s: number) => ({ 0: 'red', 1: 'green', 2: 'red' }[s] ?? 'red');
// manager 1=管理员 0=组员
const managerText = (m: number) => (Number(m) === 1 ? '管理员' : '组员');
const managerClass = (m: number) => (Number(m) === 1 ? 'green' : '');

const closePromoterModal = () => {
    promoterVisible.value = false;
    promoterList.value = [];
    promoterDeptName.value = '';
};

const handleViewPromoters = (node: any) => {
    if (!node || node.departmentId == null) return;
    promoterDeptName.value = node.departmentName || '';
    promoterList.value = [];
    promoterVisible.value = true;
    promoterLoading.value = true;
    listDepartmentPromoters(node.departmentId)
        .then((res: any) => {
            promoterLoading.value = false;
            if (res.code === 1) {
                promoterList.value = res.list || [];
            } else {
                message.error(res.tip || '加载人员信息失败');
                promoterList.value = [];
            }
        })
        .catch(() => {
            promoterLoading.value = false;
            promoterList.value = [];
        });
};

onMounted(() => {
    // 页面挂载时拉一次默认回显，badge 立刻显示可访问部门数
    fetchSelectList();
});
</script>

<style scoped lang="less">
.header-actions-wrapper {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 8px;
}

.menu-search-trigger {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 16px;
  background-color: rgba(0, 0, 0, 0.04);
  cursor: pointer;
  transition: all 0.2s;
  color: rgba(0, 0, 0, 0.55);
  font-size: 13px;
  white-space: nowrap;
  user-select: none;

  & + .menu-search-trigger {
    margin-left: 8px;
  }

  &:hover {
    background-color: rgba(0, 0, 0, 0.08);
    color: #fa8c16;
  }

  &.is-active {
    background-color: #fff7e6;
    color: #fa8c16;

    &:hover {
      background-color: #ffe7ba;
    }
  }
}

.menu-search-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 6px;
  font-size: 12px;
  line-height: 1;
  color: #fff;
  background-color: #ff4d4f;
  border-radius: 9px;
  margin-left: 2px;
}

.menu-search-icon {
  font-size: 14px;
}

.menu-search-text {
  font-size: 13px;
}

.menu-search-results {
  max-height: 420px;
  overflow-y: auto;
  margin-top: 12px;
}

.menu-search-item {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s;
  gap: 12px;

  &:hover {
    background-color: #fff7e6;
  }

  &-icon {
    font-size: 16px;
    color: #fa8c16;
    flex-shrink: 0;
  }

  &-title {
    font-size: 14px;
    color: #333;
    flex-shrink: 0;
  }

  &-path {
    font-size: 12px;
    color: #999;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
  }
}
</style>

<style lang="less">
.menu-search-modal {
  .ant-modal-content {
    padding: 16px;
  }

  .ant-empty {
    margin: 24px 0;
  }
}

.data-filter-tip {
  color: rgba(0, 0, 0, 0.55);
  font-size: 13px;
  margin-bottom: 10px;
}

.data-filter-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
  margin-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.data-filter-tree {
  max-height: 420px;
  min-height: 200px;
  overflow-y: auto;
  padding: 4px 4px 4px 0;

  &-view {
    margin: 0 4px;
    padding: 0 4px;
    font-size: 12px;
  }

  &-counts {
    margin-left: 12px;
    display: inline-flex;
    gap: 10px;
    font-size: 12px;
    color: #999;

    b {
      color: #1890ff;
      margin: 0 2px;
      font-variant-numeric: tabular-nums;
    }
  }
}

.data-filter-modal {
  .ant-modal-body {
    padding-top: 12px;
    padding-bottom: 8px;
  }
}

.dot {
  &::before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 5px;
  }

  &.green::before {
    background-color: #67c23a;
  }

  &.red::before {
    background-color: #f56c6c;
  }
}

.data-filter-summary {
  margin-top: 12px;
  padding: 8px 12px;
  background: #fafafa;
  border-radius: 4px;
  max-height: 180px;
  overflow-y: auto;

  &-title {
    font-size: 12px;
    color: #999;
    margin-bottom: 6px;
  }

  &-list {
    margin: 0;
    padding: 0;
    list-style: none;

    li {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 4px 0;
      font-size: 13px;
      color: #333;
    }
  }

  &-name {
    font-weight: 500;
  }

  &-counts {
    display: flex;
    gap: 12px;
    color: #666;

    b {
      color: #1890ff;
      margin: 0 2px;
      font-variant-numeric: tabular-nums;
    }
  }
}
</style>